import React, { useState, useEffect, useCallback } from 'react';
import { animated, useSprings, useTransition, config } from '@react-spring/web';
import { PlusIcon, KebabIcon } from '@pushpay/iconography';
import { clsx, multiply, createUseStyles } from '@pushpay/styles';
import { Button } from './button';
const actionHeightInEm = 2.5;
const actionGutterInEm = 1;
const useStyles = createUseStyles(theme => ({
  root: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    margin: multiply(theme.SPACING.GUTTER, 2),
    zIndex: theme.ELEVATION.MODAL.Z_INDEX,
    borderRadius: theme.SHAPE.CIRCULAR
  },
  button: {
    height: '3em',
    width: '3em',
    borderRadius: '1.5em',
    position: 'relative',
    zIndex: 1,
    boxShadow: theme.ELEVATION.EIGHT,
    border: 'none',
    cursor: 'pointer',
    '&:focus': {
      backgroundColor: theme.colors['product-color']
    }
  },
  closeIcon: {
    transform: 'rotate(45deg)'
  },
  iconContainer: {
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    borderRadius: theme.SHAPE.CIRCULAR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontSize: '1.5em'
  },
  action: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: `${actionHeightInEm}em`,
    alignItems: 'center',
    whiteSpace: 'nowrap',
    font: theme.TYPOGRAPHY.BODY_1
  },
  mask: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
    opacity: 0.75,
    backgroundColor: theme.colors['palette-black'],
    zIndex: -1
  }
}));

const getToggleTransition = (isOpen, length) => index => ({
  opacity: isOpen ? 1 : 0,
  y: (isOpen ? index : index - 1) * (actionHeightInEm + actionGutterInEm),
  display: isOpen ? 'flex' : 'none',
  delay: 75 * (isOpen ? index : length - index + 1)
});

const buttonTransition = {
  initial: {
    opacity: 1,
    transform: 'rotate(180deg)'
  },
  from: {
    opacity: 0,
    transform: 'rotate(0deg)'
  },
  enter: {
    opacity: 1,
    transform: 'rotate(180deg)'
  },
  leave: {
    opacity: 0,
    transform: 'rotate(360deg)'
  }
};
export const FloatingActionButton = props => {
  const {
    actions,
    classes: classesProp,
    className: classNameProp,
    'data-pp-at-target': targetId,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const classes = useStyles(classesProp);
  const className = clsx(classes.root, classNameProp);
  const [toggleSprings, setToggleSprings] = useSprings(actions.length + 1, i => ({
    from: {
      opacity: 0,
      y: i * 2.5
    },
    config: config.stiff,
    ...getToggleTransition(isOpen, actions.length + 1)
  }));
  const buttonTransitions = useTransition(isOpen, buttonTransition);
  useEffect(() => {
    setToggleSprings(getToggleTransition(isOpen, actions.length + 1));
  }, [isOpen, actions]);
  const toggle = useCallback(() => setIsOpen(current => !current), []);
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(Button, Object.assign({
    className: classes.button,
    displayStyle: "primary",
    onClick: toggle,
    "data-pp-at-target": targetId,
    type: "button"
  }, rest), buttonTransitions((buttonStyle, item) => /*#__PURE__*/React.createElement(animated.div, {
    style: buttonStyle,
    className: classes.iconContainer
  }, item ? /*#__PURE__*/React.createElement(PlusIcon, {
    className: classes.closeIcon,
    "data-pp-at-target": `${targetId}-close-icon`
  }) : /*#__PURE__*/React.createElement(KebabIcon, {
    "data-pp-at-target": `${targetId}-kebab-icon`
  })))), toggleSprings.map(({
    y,
    opacity
  }, index) => {
    if (index === 0) {
      return /*#__PURE__*/React.createElement(animated.div, {
        key: "mask",
        style: {
          opacity: opacity.to(o => o * 0.75),
          display: opacity.to(o => o > 0.01 ? 'block' : 'none')
        },
        className: classes.mask,
        onClick: toggle
      });
    }

    return /*#__PURE__*/React.createElement(animated.div, {
      key: `action-${index - 1}`,
      className: classes.action,
      style: {
        opacity,
        transform: y.to(translateY => `translate3d(0, -${translateY}em, 0)`),
        display: opacity.to(o => o ? 'block' : 'none')
      }
    }, actions[index - 1](toggle));
  }));
};